import * as React from 'react';
import styled, { css } from 'styled-components';

import { getSemanticAttributes } from '../../internals/semanticProps';
import { useControllable } from '../../internals/useControllable';
import { useId } from '../../internals/useId';
import { palette } from '../../tokens/palette';
import { rem } from '../../tokens/spacing';

/* ─────────────────────────── MobileAccordion ────────────────────────────── */

export interface NavbarMobileAccordionProps {
  /** Label text for the trigger row. */
  label: string;
  /** Highlights the trigger when any child route is active. */
  active?: boolean;
  /** Controlled expanded state. */
  expanded?: boolean;
  defaultExpanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  semanticId?: string;
  children: React.ReactNode;
}

const TRANSIENT_TRIGGER = ['$active', '$expanded'] as const;
const shouldForwardTrigger = (prop: string | number): boolean =>
  !TRANSIENT_TRIGGER.includes(prop as (typeof TRANSIENT_TRIGGER)[number]);

const StyledAccordionRoot = styled.div`
  width: 100%;
  border-bottom: 1px solid rgba(215, 215, 215, 0.5);
`;

const StyledAccordionTrigger = styled.button.withConfig({
  shouldForwardProp: shouldForwardTrigger,
})<{ $active: boolean; $expanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background: none;
  border: none;
  cursor: pointer;
  font-family: inherit;
  font-weight: 600;
  font-size: ${rem(15)};
  line-height: ${rem(19)};
  color: var(--ui-text, ${palette.Black});
  padding: ${rem(20)} ${rem(20)};
  transition: color 0.2s linear;
  text-align: left;

  ${({ $active }) =>
    $active &&
    css`
      color: var(--ui-interactive, ${palette.Purple});
    `}
`;

const TRANSIENT_CHEVRON = ['$expanded'] as const;
const shouldForwardChevron = (prop: string | number): boolean =>
  !TRANSIENT_CHEVRON.includes(prop as (typeof TRANSIENT_CHEVRON)[number]);

const StyledChevron = styled.span.withConfig({
  shouldForwardProp: shouldForwardChevron,
})<{ $expanded: boolean }>`
  font-size: ${rem(18)};
  line-height: 1;
  transition: transform 0.25s ease;
  transform: ${({ $expanded }) => ($expanded ? 'rotate(180deg)' : 'rotate(0deg)')};
  display: inline-flex;
  align-items: center;
  color: var(--ui-text-subtle, ${palette.SpanishGray});
  flex-shrink: 0;
`;

const StyledAccordionContent = styled.div`
  overflow: hidden;
  transition: max-height 0.25s ease, opacity 0.25s ease;

  &[data-state='closed'] {
    max-height: 0;
    opacity: 0;
  }

  &[data-state='open'] {
    opacity: 1;
  }
`;

const StyledAccordionList = styled.div`
  padding: 0 ${rem(10)} ${rem(10)} ${rem(20)};
  display: flex;
  flex-direction: column;
  gap: ${rem(2)};
`;

const NavbarMobileAccordion: React.FC<NavbarMobileAccordionProps> = ({
  label,
  active = false,
  expanded,
  defaultExpanded,
  onExpandedChange,
  semanticId,
  children,
}) => {
  const [isExpanded, setExpanded] = useControllable<boolean>({
    value: expanded,
    defaultValue: defaultExpanded ?? false,
    onChange: onExpandedChange,
  });

  const triggerId = useId('nav-mobile-accordion-trigger');
  const contentId = useId('nav-mobile-accordion-content');
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  const semanticProps = getSemanticAttributes(semanticId);

  React.useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    if (isExpanded) {
      el.style.maxHeight = `${el.scrollHeight}px`;
    } else {
      el.style.maxHeight = '0px';
    }
  }, [isExpanded]);

  return (
    <StyledAccordionRoot {...semanticProps}>
      <StyledAccordionTrigger
        id={triggerId}
        type="button"
        $active={active}
        $expanded={isExpanded}
        aria-expanded={isExpanded}
        aria-controls={contentId}
        onClick={() => setExpanded(!isExpanded)}
      >
        {label}
        <StyledChevron className="material-symbols-outlined" $expanded={isExpanded} aria-hidden>
          keyboard_arrow_down
        </StyledChevron>
      </StyledAccordionTrigger>

      <StyledAccordionContent
        ref={contentRef}
        id={contentId}
        role="region"
        aria-labelledby={triggerId}
        data-state={isExpanded ? 'open' : 'closed'}
      >
        <StyledAccordionList role="group">
          {children}
        </StyledAccordionList>
      </StyledAccordionContent>
    </StyledAccordionRoot>
  );
};

NavbarMobileAccordion.displayName = 'Navbar.MobileAccordion';

/* ─────────────────────────── MobileAccordionItem ────────────────────────── */

export interface NavbarMobileAccordionItemProps extends React.HTMLAttributes<HTMLDivElement> {
  active?: boolean;
  semanticId?: string;
}

const TRANSIENT_ACCORDION_ITEM = ['$active'] as const;
const shouldForwardItem = (prop: string | number): boolean =>
  !TRANSIENT_ACCORDION_ITEM.includes(prop as (typeof TRANSIENT_ACCORDION_ITEM)[number]);

const StyledAccordionItem = styled.div.withConfig({
  shouldForwardProp: shouldForwardItem,
})<{ $active: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;

  > a,
  > button,
  > span {
    font-weight: 400;
    font-size: ${rem(14)};
    line-height: ${rem(18)};
    color: var(--ui-text, ${palette.Black});
    display: flex;
    align-items: center;
    text-decoration: none;
    background: none;
    border: none;
    font-family: inherit;
    padding: ${rem(8)} ${rem(10)};
    border-radius: var(--ui-radius-md, ${rem(8)});
    cursor: pointer;
    width: 100%;
    transition: all 0.2s linear;
  }

  ${({ $active }) =>
    $active &&
    css`
      > a,
      > button,
      > span {
        background: var(--ui-interactive, ${palette.Purple});
        color: ${palette.White};
        font-weight: 500;
      }
    `}
`;

const NavbarMobileAccordionItem = React.forwardRef<HTMLDivElement, NavbarMobileAccordionItemProps>(
  function NavbarMobileAccordionItem({ active = false, semanticId, ...rest }, ref) {
    const semanticProps = getSemanticAttributes(semanticId);
    return (
      <StyledAccordionItem
        ref={ref}
        $active={active}
        data-active={active || undefined}
        {...semanticProps}
        {...rest}
      />
    );
  },
);

NavbarMobileAccordionItem.displayName = 'Navbar.MobileAccordionItem';

/* ─────────────────────────── Exports ───────────────────────────────────── */

export { NavbarMobileAccordion, NavbarMobileAccordionItem };
